import * as Sentry from '@sentry/react';

/** The markup tag-manager embeds ship with; a bare `<` is a comparison in JavaScript. */
const HTML_MARKUP = /<!--|<\s*\/?\s*script\b/i;

const LOADER = (src: string) =>
  `(function(d){var s=d.createElement('script');s.async=true;s.src=${JSON.stringify(src)};d.head.appendChild(s);})(document);`;

const reported = new Set<string>();

/**
 * The restaurant's head_scripts as the JavaScript the shop runs in one inline `<script>` through
 * Helmet, or null when it cannot run. A snippet pasted as HTML (`<script>…</script>`, an external
 * `<script src>`, an HTML comment) is unwrapped the way the backend's migration rewrote the stored
 * rows; a returning visitor's persisted franchise can still carry the old value, and Helmet's
 * appendChild threw "Unexpected token '<'" on it (SHOP-WEB-Q). What still does not parse is left
 * out, said once, instead of thrown at the visitor's page.
 *
 * Block-scoped like storefront-web's restaurant scripts: Helmet re-inserts the tag when the head
 * changes, and a top-level `let`/`const` would then throw "already been declared". `var` and
 * function declarations still reach the global scope (gtag, dataLayer).
 */
export function toRunnableHeadScript(content: string | null | undefined): string | null {
  if (!content?.trim()) {
    return null;
  }

  const script = HTML_MARKUP.test(content) ? unwrapHtml(content) : content;

  if (!script.trim()) {
    return null;
  }

  if (!isParsableJavaScript(script)) {
    reportOnce(content);

    return null;
  }

  return `{\n${script}\n}`;
}

function unwrapHtml(content: string): string {
  return content
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*?\/?>(?:\s*<\/script\s*>)?/gi, (_, src: string) =>
      LOADER(src),
    )
    .replace(/<\/?script\b[^>]*>/gi, '')
    .trim();
}

/** A SyntaxError only: anything else (eval refused by a CSP) is left for the browser to decide. */
function isParsableJavaScript(script: string): boolean {
  try {
    new Function(script);

    return true;
  } catch (error) {
    return !(error instanceof SyntaxError);
  }
}

function reportOnce(content: string): void {
  if (reported.has(content)) {
    return;
  }

  reported.add(content);
  console.warn('[head_scripts] left out: not valid JavaScript');
  Sentry.addBreadcrumb({
    category: 'head_scripts',
    level: 'warning',
    message: 'head_scripts left out: not valid JavaScript',
    data: { length: content.length, starts_with: content.trimStart().slice(0, 40) },
  });
}
